import axios from "../../axiosConfig";
import { getDisplayRoleLabel } from "../../utils/displayRole";

const withTsParam = (params = {}) => ({
  ...params,
  ts: Date.now(),
});

export const parseCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const uniqueById = (items) =>
  items.filter((item, index, collection) => {
    if (!item) return false;
    const itemKey = item?.id != null ? `id:${item.id}` : `idx:${index}`;
    return collection.findIndex((candidate, candidateIndex) => {
      if (!candidate) return false;
      const candidateKey = candidate?.id != null ? `id:${candidate.id}` : `idx:${candidateIndex}`;
      return candidateKey === itemKey;
    }) === index;
  });

const contextRoleForRoleName = (roleName) => {
  if (roleName === "network_manager" || roleName === "network_admin") return "admin";
  if (roleName === "network_viewer" || roleName === "network_user") return "viewer";
  return "";
};

const formatFullName = (firstName, lastName) =>
  [firstName, lastName].filter(Boolean).join(" ").trim();

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const organizationIdsForUser = (user = {}) => {
  const assignments = Array.isArray(user?.rbac?.assignments) ? user.rbac.assignments : [];
  const roles = []
    .concat(Array.isArray(user?.roles) ? user.roles : [])
    .concat(Array.isArray(user?.user_roles) ? user.user_roles : []);
  const memberships = Array.isArray(user?.organization_memberships) ? user.organization_memberships : [];

  return Array.from(new Set([
    user?.organization_id,
    user?.organization?.id,
    ...(Array.isArray(user?.organization_ids) ? user.organization_ids : []),
    ...assignments.flatMap((assignment) => [
      assignment?.organization_id,
      assignment?.organization?.id,
      assignment?.org_id,
      assignment?.org?.id,
    ]),
    ...roles.flatMap((role) => typeof role === "object" ? [
      role?.organization_id,
      role?.organization?.id,
      role?.org_id,
      role?.org?.id,
    ] : []),
    ...memberships.flatMap((membership) => [
      membership?.organization_id,
      membership?.organization?.id,
      membership?.org_id,
      membership?.org?.id,
    ]),
  ].map(toPositiveNumber).filter(Boolean)));
};

export const normalizeNetworkUserMembership = (membership = {}) => {
  const nestedUser = membership?.user || {};
  const nestedRole = membership?.role || {};
  const firstName = nestedUser?.first_name || membership?.firstName || membership?.first_name || "";
  const lastName = nestedUser?.last_name || membership?.lastName || membership?.last_name || "";
  const email = nestedUser?.email || membership?.email || "";
  const name = formatFullName(firstName, lastName) || membership?.name || email || "User";
  const roleName = nestedRole?.name || membership?.roleName || membership?.role_name || membership?.role || "network_viewer";

  return {
    ...membership,
    membershipId: membership?.membershipId || membership?.id,
    userId: nestedUser?.id || membership?.userId || membership?.user_id,
    firstName,
    lastName,
    name,
    email,
    roleName,
    roleLabel: getDisplayRoleLabel(roleName),
    contextRole: membership?.contextRole || membership?.context_role || contextRoleForRoleName(roleName),
    isOwner: Boolean(membership?.isOwner ?? membership?.is_owner),
  };
};

export const getNetwork = async (networkId) => {
  const response = await axios.get(`/networks/${networkId}`, { params: withTsParam() });
  const payload = response?.data || {};
  return payload?.network || payload;
};

export const getNetworkOrganizations = async (networkId) => {
  const response = await axios.get(`/networks/${networkId}/organizations`, { params: withTsParam() });
  return uniqueById(parseCollection(response?.data, ["organizations", "network_organizations"]));
};

export const getNetworkOrganization = async (networkId, organizationId) => {
  const response = await axios.get(
    `/networks/${networkId}/organizations/${organizationId}`,
    { params: withTsParam() }
  );
  const payload = response?.data || {};
  return payload?.organization || payload;
};

const buildNetworkOrganizationPayload = (payload) => ({
  organization: {
    name: payload?.name?.trim() || "",
    contact: payload?.contact?.trim() || "",
    email: payload?.email?.trim() || "",
    phone: payload?.phone?.trim() || "",
    address: payload?.address?.trim() || "",
    city: payload?.city?.trim() || "",
    state: payload?.state?.trim() || "",
    country: payload?.country?.trim() || "",
    zip: payload?.zip?.trim() || "",
  },
});

export const createNetworkOrganization = async (networkId, payload) => {
  const response = await axios.post(
    `/networks/${networkId}/organizations`,
    buildNetworkOrganizationPayload(payload)
  );
  return response?.data;
};

export const updateNetworkOrganization = async (networkId, organizationId, payload) => {
  const response = await axios.patch(
    `/networks/${networkId}/organizations/${organizationId}`,
    buildNetworkOrganizationPayload(payload)
  );
  return response?.data;
};

export const getNetworkOrganizationUsers = async (_networkId, organizationId) => {
  const response = await axios.get("/users", { params: withTsParam() });
  const users = uniqueById(parseCollection(response?.data, ["users"]));
  const scopedOrganizationId = toPositiveNumber(organizationId);

  if (!scopedOrganizationId) return [];

  return users.filter((user) => organizationIdsForUser(user).includes(scopedOrganizationId));
};

export const getNetworkUsers = async (networkId) => {
  const response = await axios.get(`/networks/${networkId}/users`, { params: withTsParam() });
  return uniqueById(parseCollection(response?.data, ["users", "network_users"])).map(normalizeNetworkUserMembership);
};

export const createNetworkUser = async (networkId, payload) => {
  const roleName = payload?.role_name || "network_viewer";
  return axios.post(`/networks/${networkId}/users`, {
    user: {
      first_name: payload?.first_name?.trim() || "",
      last_name: payload?.last_name?.trim() || "",
      email: payload?.email?.trim() || "",
    },
    roles: [
      {
        name: roleName,
        scope: "network",
        consultant_id: null,
        network_id: Number(networkId),
        organization_id: null,
      },
    ],
  });
};

export const removeNetworkUser = async (networkId, userId) => {
  return axios.delete(`/networks/${networkId}/users/${userId}`);
};

export const updateNetwork = async (networkId, payload) => {
  const response = await axios.patch(`/networks/${networkId}`, {
    name: payload?.name?.trim() || "",
    description: payload?.description?.trim() || "",
  });
  return response?.data;
};
